/**
 * Initialize user session with MSAL-like token acquisition
 * @param {object} msalInstance - MSAL instance
 * @param {Array} accounts - User accounts array
 * @param {object} loginRequest - Login request configuration
 * @returns {Promise<object>} User graph data with name and photo
 */
export const initializeUserSession = async (msalInstance, accounts, loginRequest) => {
  if (accounts && accounts.length > 0) {
    try {
      // Silently acquire an access token to call Microsoft Graph
      const response = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });

      // Set the token in session storage
      setAuthToken(response.accessToken);

      // Fetch user profile data including name and photo
      const userInfo = await fetchUserFromGraph(response.accessToken);

      // Return graph data in the format expected by Header component
      const graphData = {
        name: userInfo.name,
        photo: userInfo.photo || null, // Photo URL if available
        userInfo: userInfo // Complete user info
      };

      return graphData;
    } catch (error) {
      console.error("Error fetching MS Graph data: ", error);
      throw error;
    }
  }
  
  return null;
};

/**
 * Fetch user photo from Microsoft Graph API
 * @param {string} accessToken - Access token
 * @returns {Promise<string|null>} Base64 encoded photo or null
 */
export const fetchUserPhoto = async (accessToken) => {
  try {
    console.log('Fetching user photo from Microsoft Graph API...');
    
    const response = await fetch("https://graph.microsoft.com/v1.0/me/photo/$value", {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'image/*'
      }
    });
    
    console.log('Photo response status:', response.status);
    
    if (response.ok) {
      const blob = await response.blob();
      const photoUrl = URL.createObjectURL(blob);
      console.log('User photo fetched successfully');
      return photoUrl;
    } else if (response.status === 404) {
      console.log('User has no profile photo');
      return null;
    } else {
      console.log('Failed to fetch user photo, status:', response.status);
      return null;
    }
  } catch (error) {
    console.error("Error fetching user photo:", error);
    return null;
  }
};

/**
 * Acquire token silently using MSAL instance
 * @param {object} msalInstance - MSAL instance
 * @param {object} account - User account
 * @param {object} loginRequest - Login request configuration
 * @returns {Promise<string>} Access token
 */
export const acquireTokenSilently = async (msalInstance, account, loginRequest) => {
  try {
    const response = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: account,
    });
    return response.accessToken;
  } catch (error) {
    console.error("Error acquiring token silently:", error);
    throw error;
  }
};

/**
 * Fetch user information from Microsoft Graph API
 * @param {string} accessToken - Access token
 * @returns {Promise<object>} User information
 */
export const fetchUserFromGraph = async (accessToken) => {
  try {
    const response = await fetch("https://graph.microsoft.com/v1.0/me", {
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const userData = await response.json();
    console.log("User data from Graph:", userData);
    
    // Try to fetch user photo
    const photo = await fetchUserPhoto(accessToken);
    
    // Transform the response to match our user info structure
    const userInfo = {
      name: userData.displayName || userData.givenName || 'User',
      email: userData.mail || userData.userPrincipalName || '',
      initials: getInitials(userData.displayName || userData.givenName || 'User'),
      fullName: userData.displayName || `${userData.givenName || ''} ${userData.surname || ''}`.trim() || 'User',
      givenName: userData.givenName || '',
      familyName: userData.surname || '',
      jobTitle: userData.jobTitle || '',
      department: userData.department || '',
      id: userData.id || '',
      userPrincipalName: userData.userPrincipalName || '',
      photo: photo // Include the photo URL
    };
    
    return userInfo;
  } catch (error) {
    console.error("Error fetching user info from Graph:", error);
    throw error;
  }
};

/**
 * Get user information from stored token or fetch from Graph API
 * @param {string} token - JWT token
 * @returns {Promise<object|null>} User information
 */
export const getUserInfoFromToken = async (token) => {
  if (!token) {
    return null;
  }

  try {
    // First try to get user info from Microsoft Graph API
    const userInfo = await fetchUserFromGraph(token);
    
    // Store user info in session storage for quick access
    sessionStorage.setItem("userInfo", JSON.stringify(userInfo));
    
    return userInfo;
  } catch (error) {
    console.error('Error getting user info from token:', error);
    
    // Fallback: try to get cached user info from session storage
    const cachedUserInfo = sessionStorage.getItem("userInfo");
    if (cachedUserInfo) {
      try {
        return JSON.parse(cachedUserInfo);
      } catch (parseError) {
        console.error('Error parsing cached user info:', parseError);
      }
    }
    
    return null;
  }
};

/**
 * Get user initials from full name
 * @param {string} name - Full name
 * @returns {string} User initials
 */
export const getInitials = (name) => {
  if (!name) return 'U';
  
  const nameParts = name.trim().split(' ');
  if (nameParts.length === 1) {
    return nameParts[0].charAt(0).toUpperCase();
  }
  
  return `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase();
};

/**
 * Set auth token in session storage
 * @param {string} token - Auth token
 */
export const setAuthToken = (token) => {
  if (token) {
    sessionStorage.setItem("authToken", token);
    console.log('Auth token stored in session storage');
  } else {
    sessionStorage.removeItem("authToken");
    console.log('Auth token removed from session storage');
  }
  
  // Trigger auth state change event to notify all components
  try {
    window.dispatchEvent(new Event('authStateChanged'));
  } catch (error) {
    console.warn('Failed to dispatch auth state change event:', error);
  }
};

/**
 * Get auth token from session storage
 * @returns {string|null} Auth token
 */
export const getAuthToken = () => {
  return sessionStorage.getItem("authToken");
};

/**
 * Get current user information (synchronous version for backward compatibility)
 * @returns {object|null} Current user information
 */
export const getCurrentUser = () => {
  const cachedUserInfo = sessionStorage.getItem("userInfo");
  if (cachedUserInfo) {
    try {
      return JSON.parse(cachedUserInfo);
    } catch (error) {
      console.error('Error parsing cached user info:', error);
      return null;
    }
  }
  return null;
};

/**
 * Get current user information (async version)
 * @returns {Promise<object|null>} Current user information
 */
export const getCurrentUserAsync = async () => {
  const token = getAuthToken();
  return await getUserInfoFromToken(token);
};

/**
 * Get cached user information synchronously
 * @returns {object|null} Cached user information
 */
export const getCachedUser = () => {
  const cachedUserInfo = sessionStorage.getItem("userInfo");
  if (cachedUserInfo) {
    try {
      return JSON.parse(cachedUserInfo);
    } catch (error) {
      console.error('Error parsing cached user info:', error);
      return null;
    }
  }
  return null;
};

/**
 * Clear user session data
 */
export const clearUserSession = () => {
  sessionStorage.removeItem("authToken");
  sessionStorage.removeItem("userInfo");
};
